import { z } from "zod";
import type { Operation } from "../types";

/**
 * Integration conflicts review queue (Data Design PRD §4.6 / D5). A high-stakes
 * synced field that diverges from a locally-edited/other-source value opens an
 * `open` conflict here instead of silently overwriting; the user resolves it
 * ours / theirs / merged. Backed by `@excellent/core` `listConflicts` /
 * `resolveConflict`; the route lives at excellent/app/api/integration-conflicts.
 */

const conflictStatus = z.enum([
  "open",
  "resolved_ours",
  "resolved_theirs",
  "merged",
  "auto_resolved",
]);

const conflictSchema = z.object({
  uid: z.string(),
  urn: z.string(),
  field: z.string(),
  ours_value: z.string().nullable(),
  theirs_value: z.string().nullable(),
  ours_source: z.string().nullable(),
  theirs_source: z.string().nullable(),
  connection_id: z.number().nullable(),
  sync_run_id: z.number().nullable(),
  status: conflictStatus,
  resolved_by: z.string().nullable(),
  resolved_at: z.string().nullable(),
  created_at: z.string(),
});
export type IntegrationConflict = z.infer<typeof conflictSchema>;

// URL query params are strings; keep the schema coercion-free so the Operation's
// input/output types unify (createRoute infers a clean TQuery). connection_id is
// parsed to a number in the route handler.
const listQuery = z.object({
  status: conflictStatus.optional(),
  connection_id: z.string().optional(),
});
type ListQuery = z.infer<typeof listQuery>;

const listResponse = z.object({ conflicts: z.array(conflictSchema) });
type ListResponse = z.infer<typeof listResponse>;

const resolveBody = z.object({
  uid: z.string().min(1),
  resolution: z.enum(["ours", "theirs", "merged"]),
  merged_value: z.string().optional(),
});
type ResolveBody = z.infer<typeof resolveBody>;

const resolveResponse = z.object({ ok: z.boolean(), reason: z.string().optional() });
type ResolveResponse = z.infer<typeof resolveResponse>;

// coverage-ignore: Hono port deferred after RO-10-09 Next delete; contract retained for apiKey
const listOp: Operation<void, ListQuery, void, ListResponse> = {
  method: "GET",
  path: () => "/api/integration-conflicts",
  query: listQuery,
  response: listResponse,
};

// coverage-ignore: Hono port deferred after RO-10-09 Next delete; contract retained for apiKey
const resolveOp: Operation<void, void, ResolveBody, ResolveResponse> = {
  method: "POST",
  path: () => "/api/integration-conflicts",
  body: resolveBody,
  response: resolveResponse,
  // resolution flips status off the open queue.
  invalidates: [listOp],
};

export const integrationConflicts = {
  list: listOp,
  resolve: resolveOp,
};
